import { useState, useEffect, useRef } from 'react';
import { generateBCP } from '../services/api';
import { createApiRequest } from '../utils/apiResponse';
import { useAppNavigation } from '../utils/navigation';
import { transformIntroBCPDocument } from '../utils/introBCPTransform';

interface UseIntroBCPGenerationOptions {
  uploadedFiles: any[];
  apiResponse: any;
  isEditMode?: boolean;
  companyInfo?: {
    companyName: string;
    location: string;
    employeeCount?: string;
    businessDescription: string;
    criticalBusiness: string;
  };
}

interface IntroBCPGenerationState {
  isLoading: boolean;
  formData: { [key: string]: string };
  hasGenerated: boolean;
  error: string | null;
}

// 地域ごとの想定被害のマッピング
const getExpectedDamageByLocation = (location: string): string => {
  if (!location || location.trim().length === 0) {
    return `• 地震による建物・設備の損壊
• ライフライン（電気・ガス・水道）の停止
• 交通機関の麻痺による従業員の出勤困難
• 通信障害によるコミュニケーション不全`;
  }

  // 東京都の場合
  if (location.includes('東京') || location.includes('都心') || location.includes('関東')) {
    return `• 首都直下地震による震度6強以上の揺れ
• 建物・設備の倒壊・損壊
• 火災による延焼被害
• 電力供給停止（最大1週間）
• 都市ガス供給停止（最大1ヶ月）
• 上下水道の断絶（最大1ヶ月）
• 交通機関の完全停止による帰宅困難者の発生
• 通信回線の輻輳・断絶`;
  }
  
  // 大阪府・関西の場合
  if (location.includes('大阪') || location.includes('関西') || location.includes('京都') || location.includes('兵庫')) {
    return `• 南海トラフ地震による震度6弱以上の揺れ
• 津波による浸水被害（沿岸部）
• 建物・設備の損壊
• 液状化による地盤沈下
• 電力・ガス・水道の長期停止
• 交通インフラの機能停止
• 物流網の寸断`;
  }

  // 東北地方の場合
  if (location.includes('宮城') || location.includes('福島') || location.includes('岩手') || location.includes('東北')) {
    return `• 太平洋沖地震による強い揺れ
• 津波による甚大な被害（沿岸部）
• 建物・設備の倒壊
• 電力・通信インフラの損壊
• 冬季の場合、暖房停止による二次被害
• 交通機関の長期停止`;
  }

  // 九州地方の場合
  if (location.includes('福岡') || location.includes('熊本') || location.includes('九州')) {
    return `• 活断層による直下型地震
• 台風・豪雨による複合災害
• 建物・設備の損壊
• 土砂災害の危険性
• インフラの機能停止
• 物流拠点の被災による供給網の混乱`;
  }

  // その他の地域（デフォルト）
  return `• 震度6弱以上の地震による建物・設備の損壊
• ライフライン（電気・ガス・水道）の停止（最大2週間）
• 交通機関の運行停止による従業員の出勤困難
• 通信障害による業務連絡の困難
• サプライチェーンの寸断`;
};

// 業種に応じた重要事業の生成
const generatePriorityBusiness = (businessContent: string, _companyName: string): string => {
  // 製造業の場合
  if (businessContent.includes('製造') || businessContent.includes('生産')) {
    return `重要事業①：
【事業・サービス・製品】
  • 主力製品の製造・供給
  • 既存顧客への製品納入
  • 品質保証・アフターサービス
【目標復旧時間】
  • 2週間以内
【現地で復旧する場合に必要な人・モノ・資金・情報 等】
  • 技術者・作業員（最低限の生産体制）
  • 製造設備・検査機器
  • 原材料・部品の在庫
  • 生産管理システム・顧客データ
  • 電力・工業用水
【現地復旧以外の対応策】
  • 協力工場での代替生産
  • 在庫品による当面の対応
  • 他拠点からの製品供給`;
  }

  // 販売・商社の場合
  if (businessContent.includes('販売') || businessContent.includes('商社') || businessContent.includes('卸')) {
    return `重要事業①：
【事業・サービス・製品】
  • 主要顧客への商品供給
  • 受発注業務の継続
  • 在庫管理・配送手配
【目標復旧時間】
  • 3日以内
【現地で復旧する場合に必要な人・モノ・資金・情報 等】
  • 営業担当者・事務スタッフ
  • 受発注システム・顧客データベース
  • 通信手段（電話・インターネット）
  • 倉庫・在庫商品
【現地復旧以外の対応策】
  • 他拠点での業務代替
  • クラウドシステムによるリモート対応
  • 提携倉庫からの直送`;
  }

  // IT・サービス業の場合
  if (businessContent.includes('システム') || businessContent.includes('ソフト') || businessContent.includes('IT')) {
    return `重要事業①：
【事業・サービス・製品】
  • システム保守・運用サービス
  • 顧客サポート業務
  • 開発プロジェクトの継続
【目標復旧時間】
  • 24時間以内
【現地で復旧する場合に必要な人・モノ・資金・情報 等】
  • エンジニア・サポート要員
  • サーバー・ネットワーク機器
  • 開発環境・顧客データ
  • 電力・通信回線
【現地復旧以外の対応策】
  • クラウド環境での業務継続
  • リモートワークによる対応
  • データセンターの活用`;
  }

  // デフォルト
  return `重要事業①：
【事業・サービス・製品】
  • 主要事業の継続
  • 既存顧客へのサービス提供
  • 重要業務の遂行
【目標復旧時間】
  • 1週間以内
【現地で復旧する場合に必要な人・モノ・資金・情報 等】
  • 最小限の従業員
  • 事業継続に必要な設備
  • 運転資金
  • 顧客情報・業務データ
  • 電力・通信インフラ
【現地復旧以外の対応策】
  • 代替拠点での業務実施
  • 外部委託の活用
  • 在宅勤務体制の構築`;
};

// 不足事項の生成（固定テキスト）
const generateShortageItems = (_businessContent: string): string => {
  return `• 業務の多能工化による代替人員の育成
• 新たな仕入先・委託先の確保
• 事業所や倉庫の被災に備えた別拠点(代替地)の検討
• 従業員が長期間にわたって出社ができない場合の対応
• 衛星通信等、通信手段の複数化`;
};

export const useIntroBCPGeneration = ({ uploadedFiles, apiResponse, isEditMode = false, companyInfo }: UseIntroBCPGenerationOptions) => {
  const navigation = useAppNavigation();
  const hasExecutedRef = useRef(false);

  const [state, setState] = useState<IntroBCPGenerationState>({
    isLoading: false,
    formData: {},
    hasGenerated: false,
    error: null
  });

  const generateIntroBCPFromFiles = async () => {
    if (hasExecutedRef.current || state.isLoading) {
      return;
    }
    
    hasExecutedRef.current = true;
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      // APIレスポンスから情報を抽出
      const extractedInfo = apiResponse?.extracted_info || apiResponse?.merged_extracted_info || {};
      const mergedInfo = apiResponse?.merged_extracted_info || {};
      
      // 各情報を抽出（mergedInfoを優先）
      const companyName = mergedInfo.company_name || extractedInfo.company_name || '';
      const address = mergedInfo.address || extractedInfo.address || '';
      const businessContent = mergedInfo.business_content || extractedInfo.business_content || '';
      const mainBusiness = mergedInfo.main_business || extractedInfo.main_business || '';
      const representatives = mergedInfo.representatives || extractedInfo.representatives || [];
      // representatives can be array of strings or array of objects {name, title}
      const representative = mergedInfo.representative || extractedInfo.representative ||
                            (representatives.length > 0
                              ? typeof representatives[0] === 'string'
                                ? representatives[0]
                                : representatives[0]?.name || ''
                              : '');

      console.log('🏢 Extracted data for Intro BCP:', {
        companyName,
        address,
        businessContent,
        mainBusiness,
        representative
      });

      // ファイル名からのフォールバック
      const filename = apiResponse?.filename || 'Sample Company';
      const finalCompanyName = companyName || filename.replace('.pdf', '').split(' ')[0] || '清島商事株式会社';
      const finalAddress = address || '〒000-0000 東京都〇〇区〇〇1-1-1';
      
      // BCPリクエストデータを準備
      const requestData = {
        company_name: finalCompanyName,
        business_type: 'Manufacturing',
        location: finalAddress,
        tenant_id: apiResponse?.tenant_id || 'default',
        bcp_type: 'introductory',  // 超入門版を指定
        include_hazard_analysis: true,  // ハザードマップ解析を含める
        extracted_info: {
          company_name: companyName,
          address: address,
          business_content: businessContent,
          main_business: mainBusiness,
          representative: representative
        }
      };
      
      console.log('🚀 Intro BCP Request Data:', requestData);
      
      // API呼び出し（BCPCreatePageと同じAPIを使用）
      const responseData = await createApiRequest<{ bcp_document?: string }>({
        request: () => generateBCP(requestData),
        onSuccess: (data) => {
          console.log('📦 Intro BCP API Response:', data);
        },
        onError: (error) => {
          console.error('Intro BCP generation failed:', error);
          setState(prev => ({
            ...prev,
            error: error.userMessage || error.message,
            isLoading: false
          }));
        }
      });

      if (responseData && responseData.bcp_document) {
        // API応答からセクションを抽出
        const extractedData = transformIntroBCPDocument(responseData.bcp_document);

        console.log('📄 Extracted data from AI:', extractedData);

        // フォールバック値を準備
        const fallbackPriorityBusiness = generatePriorityBusiness(businessContent || '', finalCompanyName);
        const fallbackShortageItems = generateShortageItems(businessContent || '');
        const fallbackExpectedDamage = getExpectedDamageByLocation(finalAddress);

        // AI生成があればそれを使用、なければフォールバック
        const usedAIFor41 = !!extractedData.priority_business;
        const usedAIFor42 = !!extractedData.shortage_items;
        const usedAIFor21 = !!extractedData.expected_damage;

        console.log(`🤖 AI generation status: 4-1=${usedAIFor41}, 4-2=${usedAIFor42}, 2-1=${usedAIFor21}`);

        // 超入門版用のフォームデータを生成
        const introFormData: { [key: string]: string } = {
          // 1-1. 事業者名
          company_info: finalCompanyName,

          // 1-2. 所在地
          business_continuity: finalAddress,

          // 1-3. 基本方針（デフォルト値を使用）
          basic_policy: '①従業員とその家族の安全を守る\n②現在の事業規模を維持し、従業員の雇用を守る\n③事業を早期復旧し、顧客の信用を守る\n④帰宅困難者や地域住民を支援し、地域社会に貢献する',

          // 2-1. 所在地で想定される地震被害 (AI生成を優先)
          expected_damage: extractedData.expected_damage || fallbackExpectedDamage,

          // 3-1. 地震直後の行動（デフォルト値を使用）
          earthquake_response: '【就業時間中】\n① 自身(来客者)の安全確保\n（落下物に注意、EVに乗らない、消火等）\n② (必要に応じて)避難の実施\n（避難情報に留意し、避難場所等に避難）\n③ 家族の安否確認\n④ 会社へ安否報告\n⑤ 安全な場所に留まり指示を待つ\n\n【就業時間外】\n① 自身の安全確保\n② (必要に応じて)避難の実施\n（避難情報に留意し、最寄りの避難所に避難）\n③ 会社へ安否報告、指示を待つ\n④ 安全な場所に留まり指示を待つ',

          // 3-2. 対策本部（代表者名を挿入）
          disaster_response_team: `本部長：{役職名を入力}\n\n【初期対応チーム】\n・メンバー：{役職名を入力}\n・役割：安否情報集約、各設備の被害状況集約、従業員の対応計画、負傷者対応\n\n【復旧対応チーム】\n・メンバー：{役職名を入力}\n・役割：重要事業の実施体制検討、システム状況対応、資金繰り管理、各種情報収集（災害情報、協力会社、他社の状況等）、情報発信`,

          // 4-1. 重要事業の選定・復旧計画 (AI生成を優先)
          priority_business: extractedData.priority_business || fallbackPriorityBusiness,

          // 4-2. 重要事業継続にあたっての不足事項
          shortage_items: extractedData.shortage_items || fallbackShortageItems,

          // 5-1. チェックリスト（デフォルト値を使用）
          checklist: '・備蓄品の整備\n・従業員の安否確認方法の確立・周知\n・重要情報のバックアップ、機器の整備（PC／複合機／電話／非常用電源／テレワーク対応機器等）\n・落下物の防止対策、耐震対策\n・一斉帰宅抑制や家族間の安否確認、自宅での備蓄品整備に係る啓発'
        };
        
        console.log('✨ Generated Intro BCP form data:', introFormData);
        setState(prev => ({
          ...prev,
          formData: introFormData,
          isLoading: false,
          hasGenerated: true
        }));
      } else {
        throw new Error('No response data received');
      }
    } catch (error) {
      console.error('Error generating intro BCP:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'BCP生成に失敗しました'
      }));
    }
  };

  useEffect(() => {
    // 編集モードの場合はBCP生成をスキップ
    if (isEditMode) {
      return;
    }

    // uploadedFilesもcompanyInfoもない場合はアップロードページにリダイレクト
    if (uploadedFiles.length === 0 && !companyInfo) {
      if (!navigation.requireUploadedFiles(uploadedFiles)) {
        return;
      }
    }

    // まだ生成していない場合のみ実行
    if (!hasExecutedRef.current) {
      generateIntroBCPFromFiles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    ...state,
    regenerate: () => {
      hasExecutedRef.current = false;
      generateIntroBCPFromFiles();
    }
  };
};
